import type { FC } from 'react';
import { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, List, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { MapFormLocation } from '../../../modules/Map/feature/LocationMap';
import type { DrawerNavigationProps } from '../../../state/drawer';
import {
  isEqualLocations,
  locationsControlState,
  updateLocationsControl,
  TypeLocation,
} from '../../../state/locationsForm';
import type { LocationControlItem } from '../../../state/locationsForm';
import { Button } from '../../base';

type LocationsManagerModalProps = Partial<DrawerNavigationProps>;

const getLocationKey = (location: LocationControlItem): string => {
  if ('key' in location) return (location as { key: string }).key;
  return String((location as { id: number }).id);
};

export const LocationsManagerModal: FC<LocationsManagerModalProps> = ({ onEndAction, goForward, goBack }) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useRecoilState(locationsControlState);
  const { showConfirm, confirmModal } = useConfirmDialog({ content: t('confirm-delete-location') });

  const [localLocations, setLocalLocations] = useState<LocationControlItem[]>([]);

  useEffect(() => {
    setLocalLocations(locations);
  }, [locations]);

  const handleTitleChange = (location: LocationControlItem, value: string) => {
    setLocalLocations((prev) => updateLocationsControl(prev, location, (item) => ({ ...item, title: value })));
  };

  const handleDeleteFromList = async (location: LocationControlItem) => {
    const isOk = await showConfirm();
    if (isOk) {
      if (location.type === TypeLocation.New) {
        setLocalLocations((prev) => prev.filter((l) => !isEqualLocations(l, location)));
      } else {
        setLocalLocations((prev) =>
          updateLocationsControl(
            prev,
            location,
            (item) =>
              ({
                ...item,
                type: TypeLocation.Delete,
              } as LocationControlItem),
          ),
        );
      }
    }
  };

  const handleApply = () => {
    setLocations(localLocations);
    onEndAction?.();
  };

  const handleCancel = () => {
    onEndAction?.();
  };

  const visibleLocations = localLocations.filter((l) => l.type !== TypeLocation.Delete);

  const duplicateTitleKeys = new Set<string>(
    visibleLocations
      .filter((loc) =>
        visibleLocations.some(
          (other) =>
            !isEqualLocations(other, loc) && other.title.trim().toLowerCase() === loc.title.trim().toLowerCase(),
        ),
      )
      .map(getLocationKey),
  );

  return (
    <Stack spacing={2} flexGrow={1} justifyContent='space-between'>
      <Stack>
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
          <Typography variant='h4' sx={{ flex: 1 }}>
            {t('manage-locations')}
          </Typography>
        </Stack>

        <Box mt={2}>
          {visibleLocations.length === 0 ? (
            <Stack py={2} alignItems='center'>
              <Typography>{t('no-locations')}</Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {visibleLocations.map((location) => {
                const key = getLocationKey(location);
                return (
                  <Box component='li' key={key} display='flex' alignItems='center' py={1}>
                    <TextField
                      fullWidth
                      size='small'
                      label={t('title')}
                      value={location.title}
                      onChange={(e) => handleTitleChange(location, e.target.value)}
                      error={duplicateTitleKeys.has(key)}
                      helperText={duplicateTitleKeys.has(key) ? t('location-name-already-exists') : undefined}
                    />

                    <Stack direction='row'>
                      <IconButton
                        color='primary'
                        onClick={() =>
                          goForward?.(<MapFormLocation locationForm={location} onEndAction={() => goBack?.()} />)
                        }
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge='end'
                        onClick={() => handleDeleteFromList(location)}
                        color='error'
                        disabled={!location.canRemove}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </List>
          )}
        </Box>
      </Stack>
      <Stack direction='column' spacing={1}>
        <Button fullWidth onClick={handleApply} variant='contained' disabled={duplicateTitleKeys.size > 0}>
          {t('apply')}
        </Button>
        <Button fullWidth onClick={handleCancel} color='inherit' variant='outlined'>
          {t('cancel')}
        </Button>
      </Stack>
      {confirmModal}
    </Stack>
  );
};
